import { Box, Text, alpha, styled } from '@ltvco/refresh-lib/theme';

import { McpImage, PlainButton } from 'components/shared/bumperMcp/styled';

export const PageRoot = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('md')]: { padding: '48px 40px' },
}));

export const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),
}));

export const PageIcon = styled(McpImage)({ width: 36, height: 36 });

export const PageTitle = styled('h1')({
  margin: 0,
  fontSize: 34,
  lineHeight: 1.15,
  fontWeight: 700,
  letterSpacing: '0.06em',
});

export const SectionTitle = styled('h2')(({ theme }) => ({
  margin: theme.spacing(0, 0, 1),
  fontSize: 30,
  lineHeight: 1.15,
  fontWeight: 700,
}));

export const SectionText = styled(Text)(({ theme }) => ({
  fontSize: 16,
  lineHeight: '24px',
  marginBottom: theme.spacing(3.5),
}));

export const HeroRoot = styled(Box)({ maxWidth: 640 });

export const ClientGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3.5),
}));

export const ClientCard = styled(PlainButton)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: '28px 20px',
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'background 200ms cubic-bezier(.22,1,.36,1)',
  '&:hover:not(:disabled)': { backgroundColor: theme.palette.grey[100] },
  '&:disabled': { cursor: 'default' },
}));

export const ClientName = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  fontSize: 24,
  fontWeight: 700,
}));

export const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'available',
})<{ available: boolean }>(({ theme, available }) => ({
  padding: '6px 16px',
  borderRadius: '100px',
  fontSize: 14,
  fontWeight: 700,
  backgroundColor: available
    ? alpha(theme.palette.success.main, 0.12)
    : theme.palette.action.hover,
  color: available ? theme.palette.success.dark : theme.palette.text.secondary,
}));

export const CapabilitiesLabel = styled(Text)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
  fontSize: 14,
  fontWeight: 700,
}));

export const CapabilityRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.75),
}));

export const CapabilityText = styled(Text)({
  fontSize: 15,
  lineHeight: '22px',
});

export const GetStartedButton = styled(PlainButton)(({ theme }) => ({
  justifyContent: 'center',
  height: 48,
  padding: theme.spacing(0, 4),
  borderRadius: '100px',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.common.white,
  fontSize: 16,
  fontWeight: 700,
  '&:hover': { backgroundColor: theme.palette.grey[900] },
}));

export const BackButton = styled(PlainButton)(({ theme }) => ({
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  fontSize: 14,
  '&:hover': { color: theme.palette.primary.main },
}));

export const ClientPickerLabel = styled(Text)(({ theme }) => ({
  marginBottom: theme.spacing(1.25),
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 700,
}));

export const ClientPicker = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  gap: 2,
  maxWidth: '100%',
  overflowX: 'auto',
  padding: 4,
  marginBottom: theme.spacing(4),
  borderRadius: '100px',
  backgroundColor: theme.palette.action.hover,
}));

export const ClientTab = styled(PlainButton, {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
  gap: theme.spacing(1),
  flexShrink: 0,
  height: 44,
  padding: theme.spacing(0, 1.75),
  borderRadius: '100px',
  border: `1px solid ${selected ? theme.palette.divider : 'transparent'}`,
  backgroundColor: selected ? theme.palette.common.white : 'transparent',
  color: selected ? theme.palette.text.primary : theme.palette.text.secondary,
  fontSize: 14,
  fontWeight: selected ? 700 : 400,
  '&:hover:not(:disabled)': { color: theme.palette.text.primary },
  '&:disabled': { cursor: 'default' },
}));

export const SetupLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  gap: theme.spacing(5),
}));

export const StepsColumn = styled(Box)({ flex: '1 1 360px', minWidth: 0 });

export const IllustrationPanel = styled(Box)(({ theme }) => ({
  flex: '1 1 320px',
  minWidth: 0,
  overflow: 'hidden',
  padding: '24px 0 0 24px',
  borderRadius: '12px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
}));

export const StepRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

export const StepRail = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const StepNumber = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '100px',
  border: `1.5px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  fontSize: 13,
  fontWeight: 700,
}));

export const StepLine = styled(Box)(({ theme }) => ({
  width: '1.5px',
  flex: 1,
  margin: '4px 0',
  backgroundColor: alpha(theme.palette.primary.main, 0.2),
}));

export const StepBody = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<{ isLast: boolean }>(({ theme, isLast }) => ({
  flex: 1,
  minWidth: 0,
  paddingBottom: isLast ? 0 : theme.spacing(3),
}));

export const StepTitle = styled(Text)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
  fontSize: 18,
  fontWeight: 700,
}));

export const StepText = styled(Text)({ fontSize: 15, lineHeight: '22px' });

export const StepNote = styled(Text)(({ theme }) => ({
  marginTop: theme.spacing(0.75),
  color: theme.palette.text.secondary,
  fontSize: 13,
}));

export const CopyableRoot = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  maxWidth: '100%',
  marginTop: theme.spacing(1.25),
  padding: '8px 8px 8px 14px',
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '10px',
}));

export const CopyableValueText = styled(Text)({
  fontSize: 13,
  fontFamily: 'ui-monospace, monospace',
  overflowWrap: 'anywhere',
});

export const CopyActionButton = styled(PlainButton)(({ theme }) => ({
  flexShrink: 0,
  height: 30,
  padding: theme.spacing(0, 1.5),
  borderRadius: '100px',
  backgroundColor: theme.palette.common.white,
  boxShadow: `inset 0 0 0 1px ${theme.palette.divider}`,
  fontSize: 13,
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

export const OpenConnectorsLink = styled('a')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  height: 46,
  marginTop: theme.spacing(3.5),
  padding: theme.spacing(0, 2.75),
  borderRadius: '100px',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontSize: 15,
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': { backgroundColor: theme.palette.primary.dark },
}));
